import React, { useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";

import AIExhibitNarrationPressable from "../audio/AIExhibitNarrationPressable";
import { useAIExhibitFixedNarration } from "../audio/useAIExhibitFixedNarration";
import { AI_EXHIBIT_COLORS as C } from "../aiExhibitTheme";
import { getAIExhibitBatch3Copy } from "../content/aiExhibitBatch3Content";

function OptionCard({ option, onPress }) {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => ({
        padding: 16,
        borderRadius: 15,
        borderWidth: 1,
        borderColor: C.border,
        backgroundColor: pressed ? C.accentSoft : C.surfaceRaised,
      })}
    >
      <View style={{ flexDirection: "row", justifyContent: "space-between", gap: 12 }}>
        <Text style={{ color: C.text, fontSize: 19, fontWeight: "900" }}>{option.token}</Text>
        <Text style={{ color: C.accent, fontSize: 19, fontWeight: "900" }}>{option.probability}%</Text>
      </View>
      <View style={{ height: 11, marginTop: 10, borderRadius: 999, backgroundColor: C.surface, overflow: "hidden" }}>
        <View style={{ width: `${option.probability}%`, height: "100%", borderRadius: 999, backgroundColor: C.accent }} />
      </View>
    </Pressable>
  );
}

export default function NextTokenScreen({
  navigate,
  language = "en",
  audioEnabled = false,
}) {
  const { playNarration } = useAIExhibitFixedNarration({
    audioEnabled,
    language,
  });
  const t = getAIExhibitBatch3Copy(language).nextToken;
  const [firstToken, setFirstToken] = useState(null);
  const [secondToken, setSecondToken] = useState(null);

  const secondOptions = useMemo(
    () => (firstToken ? t.secondByToken[firstToken] || [] : []),
    [firstToken, t.secondByToken]
  );

  const context = [
    t.first.prompt,
    firstToken,
    secondToken,
  ]
    .filter(Boolean)
    .join(" ");

  const reset = () => {
    setFirstToken(null);
    setSecondToken(null);
  };

  return (
    <View style={{ padding: 28 }}>
      <Text style={{ color: C.accent, fontSize: 13, fontWeight: "900", letterSpacing: 1.2 }}>{t.eyebrow}</Text>
      <Text style={{ color: C.text, fontSize: 44, lineHeight: 50, fontWeight: "900", marginTop: 8 }}>{t.title}</Text>
      <AIExhibitNarrationPressable
        audioEnabled={audioEnabled}
        onPress={() => playNarration("next_token_overview")}
        style={{ marginTop: 8, maxWidth: 950, paddingRight: 34 }}
      >
        <Text style={{ color: C.muted, fontSize: 18, lineHeight: 27 }}>{t.subtitle}</Text>
      </AIExhibitNarrationPressable>

      <View style={{ marginTop: 24, padding: 22, borderRadius: 18, borderWidth: 1, borderColor: C.accent, backgroundColor: C.accentSoft }}>
        <Text style={{ color: C.accent, fontSize: 13, fontWeight: "900" }}>{t.context.toUpperCase()}</Text>
        <Text style={{ color: C.text, fontSize: 31, lineHeight: 41, fontWeight: "900", marginTop: 10 }}>
          {context}
          {!secondToken ? " ___" : ""}
        </Text>
      </View>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 18, marginTop: 18 }}>
        <View style={{ flex: 1, minWidth: 380, padding: 20, borderRadius: 18, borderWidth: 1, borderColor: C.border, backgroundColor: C.surfaceRaised }}>
          <Text style={{ color: C.text, fontSize: 20, fontWeight: "900" }}>{t.choose}</Text>
          <Text style={{ color: C.muted, fontSize: 13, lineHeight: 20, marginTop: 5 }}>{t.illustrative}</Text>

          <View style={{ gap: 10, marginTop: 16 }}>
            {!firstToken
              ? t.first.options.map((option) => (
                  <OptionCard
                    key={option.token}
                    option={option}
                    onPress={() => {
                      setFirstToken(option.token);
                      playNarration("next_token_context_changes");
                    }}
                  />
                ))
              : !secondToken
              ? secondOptions.map((option) => (
                  <OptionCard
                    key={option.token}
                    option={option}
                    onPress={() => {
                      setSecondToken(option.token);
                      playNarration("next_token_context_changes");
                    }}
                  />
                ))
              : (
                <View style={{ padding: 18, borderRadius: 15, backgroundColor: C.surface }}>
                  <Text style={{ color: C.success, fontSize: 19, fontWeight: "900" }}>
                    {language === "es" ? "Dos pasos de generación completados." : "Two generation steps complete."}
                  </Text>
                </View>
              )}
          </View>
        </View>

        <View style={{ flex: 1, minWidth: 380, padding: 20, borderRadius: 18, borderWidth: 1, borderColor: C.border, backgroundColor: C.surfaceRaised }}>
          {!firstToken ? (
            <Text style={{ color: C.muted, fontSize: 17, lineHeight: 27 }}>
              {language === "es"
                ? "El modelo todavía no ha añadido ningún token. Elige una posibilidad y observa cómo cambia el siguiente conjunto."
                : "The model has not added a token yet. Choose one possibility and watch the next distribution change."}
            </Text>
          ) : (
            <>
              <Text style={{ color: C.accent, fontSize: 13, fontWeight: "900" }}>{t.chosen.toUpperCase()}</Text>
              <Text style={{ color: C.text, fontSize: 30, fontWeight: "900", marginTop: 7 }}>
                “{secondToken || firstToken}”
              </Text>
              <Text style={{ color: C.text, fontSize: 16, lineHeight: 25, marginTop: 12 }}>{t.changed}</Text>
            </>
          )}

          <Pressable
            onPress={reset}
            style={({ pressed }) => ({
              marginTop: 18,
              alignSelf: "flex-start",
              minHeight: 50,
              paddingHorizontal: 16,
              paddingVertical: 11,
              borderRadius: 13,
              borderWidth: 1,
              borderColor: C.border,
              backgroundColor: pressed ? C.accentSoft : C.surface,
            })}
          >
            <Text style={{ color: C.text, fontSize: 15, fontWeight: "900" }}>↻ {t.reset}</Text>
          </Pressable>
        </View>
      </View>

      <AIExhibitNarrationPressable
        audioEnabled={audioEnabled}
        onPress={() => playNarration("next_token_generation_loop")}
        style={{ marginTop: 18, padding: 18, borderRadius: 17, borderWidth: 1, borderColor: C.border, backgroundColor: C.surfaceRaised }}
      >
        <Text style={{ color: C.text, fontSize: 20, fontWeight: "900" }}>{t.loopTitle}</Text>
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
          {t.loop.map((item) => (
            <View key={item} style={{ flex: 1, minWidth: 190, padding: 14, borderRadius: 14, backgroundColor: C.surface }}>
              <Text style={{ color: C.text, fontSize: 15, lineHeight: 22, fontWeight: "800", textAlign: "center" }}>{item}</Text>
            </View>
          ))}
        </View>
      </AIExhibitNarrationPressable>

      <AIExhibitNarrationPressable
        audioEnabled={audioEnabled}
        onPress={() => playNarration("next_token_not_just_autocomplete")}
        style={{ marginTop: 14, padding: 16, borderRadius: 15, borderLeftWidth: 5, borderWidth: 1, borderColor: C.border, borderLeftColor: C.warning, backgroundColor: C.accentSoft }}
      >
        <Text style={{ color: C.text, fontSize: 16, lineHeight: 25 }}>{t.notJust}</Text>
      </AIExhibitNarrationPressable>

      <Pressable
        onPress={() => navigate("llm_assistant")}
        style={({ pressed }) => ({
          marginTop: 20,
          alignSelf: "flex-start",
          minHeight: 58,
          paddingHorizontal: 22,
          paddingVertical: 14,
          borderRadius: 16,
          borderWidth: 1,
          borderColor: C.accent,
          backgroundColor: pressed ? C.surfaceRaised : C.accentSoft,
        })}
      >
        <Text style={{ color: C.text, fontSize: 17, fontWeight: "900" }}>{t.next} →</Text>
      </Pressable>
    </View>
  );
}
